import { Modal, Select, Text, LegacyStack, IndexTable, Button, Banner, Divider } from '@shopify/polaris';
import { useContext, useEffect, useMemo, useState } from 'react';
import ProductFindCard from './ProductFindCard';
import { getProducts, postReclassify, postReclassifyLosses } from '../../../services/productService';
import ToastContext from '../../../context/ToastContext';
import { SET_ERROR, SET_OK } from '../../../context/ToastReducer';
import ProductMiniStripe from './ProductMiniStripe';

// Depósito virtual de extraviados (cuenta 390) que la ficha añade a product.stock/deposits.
const LOST_DEPOSIT_ID = -390;

// Stock por talla (variante) en un depósito concreto. Para el depósito de extraviados el "qty"
// es el saldo 390 CON SIGNO (positivo = falta/perdido, negativo = sobra/aparecido).
const stockAt = (product, depositId) =>
  product.variants.map((v) => {
    const s = (product.stock || []).find((st) => st.barcode === v.barcode && st.deposit.id === depositId);
    return { barcode: v.barcode, title: v.title, qty: s ? s.qty : 0 };
  });

// Acerca `raw` a 0 sin pasarse de `bal`, conservando el signo de `bal`.
const clampTowardsZero = (raw, bal) => {
  const v = raw === '' || raw === '-' ? 0 : parseInt(raw, 10);
  if (Number.isNaN(v)) return 0;
  return bal >= 0 ? Math.max(0, Math.min(v, bal)) : Math.min(0, Math.max(v, bal));
};

const ReclassTable = ({ sourceRows, targetProduct, moved, setMoved, mapping, setMapping, isLosses }) => {
  const headings = [{ title: '' }, ...sourceRows.map((s) => ({ title: s.title })), { title: 'TOTAL' }];
  const qtyOf = (barcode) => moved.find((m) => m.barcode === barcode)?.qty ?? 0;
  const setQty = (barcode, qty) => setMoved([...moved.filter((m) => m.barcode !== barcode), { barcode, qty }]);

  const targetOptions = [
    { label: '— Talla', value: '' },
    ...targetProduct.variants.map((v) => ({ label: v.title, value: v.barcode })),
  ];

  // Recuento de destinos para marcar los duplicados.
  const destinoCounts = {};
  sourceRows.forEach((s) => {
    const t = mapping[s.barcode];
    if (t) destinoCounts[t] = (destinoCounts[t] || 0) + 1;
  });

  const totalInicial = sourceRows.reduce((a, s) => a + s.qty, 0);
  const totalMovido = sourceRows.reduce((a, s) => a + qtyOf(s.barcode), 0);

  return (
    <IndexTable headings={headings} itemCount={4} selectable={false} lastColumnSticky condensed={false}>
      {/* Fila 1: selector de talla destino por columna */}
      <IndexTable.Row subdued selected={false}>
        <IndexTable.Cell>
          <Text variant="headingXs" fontWeight="bold">
            Talla destino
          </Text>
        </IndexTable.Cell>

        {sourceRows.map((s, i) => {
          const value = mapping[s.barcode] ?? '';
          const dup = value && destinoCounts[value] > 1;
          return (
            <IndexTable.Cell key={i}>
              <div style={{ minWidth: 50 }}>
                <Select
                  labelHidden
                  label={s.title}
                  options={targetOptions}
                  value={value}
                  onChange={(val) => setMapping({ ...mapping, [s.barcode]: val })}
                  error={dup ? true : undefined}
                />
              </div>
            </IndexTable.Cell>
          );
        })}
        <IndexTable.Cell />
      </IndexTable.Row>

      {/* Fila 2: stock inicial de la fuente */}
      <IndexTable.Row subdued selected={false}>
        <IndexTable.Cell>
          <Text variant="headingXs" fontWeight="bold">
            {isLosses ? 'Extraviados' : 'Stock inicial'}
          </Text>
        </IndexTable.Cell>
        {sourceRows.map((s, i) => (
          <IndexTable.Cell key={i}>
            <Text alignment="center" variant="headingXs">
              {s.qty}
            </Text>
          </IndexTable.Cell>
        ))}
        <IndexTable.Cell>
          <Text alignment="center" fontWeight="bold" variant="headingXs">
            {totalInicial}
          </Text>
        </IndexTable.Cell>
      </IndexTable.Row>

      {/* Fila 3: movimiento (input abierto siempre) */}
      <IndexTable.Row subdued selected={false}>
        <IndexTable.Cell>
          <Text variant="headingXs" fontWeight="bold">
            Movimiento
          </Text>
        </IndexTable.Cell>
        {sourceRows.map((s, i) => (
          <IndexTable.Cell key={i}>
            <div className="flex justify-center text-center">
              <input
                type="number"
                value={qtyOf(s.barcode)}
                style={{ width: '40px', borderBottom: '1px solid black', textAlign: 'end' }}
                onChange={(e) => setQty(s.barcode, clampTowardsZero(e.target.value, s.qty))}
              />
            </div>
          </IndexTable.Cell>
        ))}
        <IndexTable.Cell>
          <Text alignment="center" fontWeight="bold" variant="headingXs">
            {totalMovido}
          </Text>
        </IndexTable.Cell>
      </IndexTable.Row>

      {/* Fila 4: stock final proyectado */}
      <IndexTable.Row subdued selected={false}>
        <IndexTable.Cell>
          <Text variant="headingXs" fontWeight="bold">
            Stock final
          </Text>
        </IndexTable.Cell>
        {sourceRows.map((s, i) => (
          <IndexTable.Cell key={i}>
            <Text alignment="center" variant="headingXs">
              {s.qty - qtyOf(s.barcode)}
            </Text>
          </IndexTable.Cell>
        ))}
        <IndexTable.Cell>
          <Text alignment="center" fontWeight="bold" variant="headingXs">
            {totalInicial - totalMovido}
          </Text>
        </IndexTable.Cell>
      </IndexTable.Row>
    </IndexTable>
  );
};

const ReclassifyModal = ({ open, onClose, product, onDone }) => {
  const [, dispatchToast] = useContext(ToastContext);
  const [sourceId, setSourceId] = useState('');
  const [targetProduct, setTargetProduct] = useState(null);
  const [optionList, setOptionList] = useState([]);
  const [moved, setMoved] = useState([]); // [{ barcode, qty }] por talla de la FUENTE (con signo)
  const [mapping, setMapping] = useState({}); // { sourceBarcode: targetBarcode }
  const [loading, setLoading] = useState(false);

  // Fuentes disponibles: depósitos reales con stock + extraviados (si tienen saldo != 0).
  const sourceOptions = useMemo(() => {
    if (!product) return [];
    return (product.deposits || [])
      .filter((d) => stockAt(product, d.id).some((s) => s.qty !== 0))
      .map((d) => ({ label: d.name, value: String(d.id) }));
  }, [product]);

  const isLosses = Number(sourceId) === LOST_DEPOSIT_ID;
  const sourceRows = product && sourceId ? stockAt(product, Number(sourceId)).filter((s) => s.qty !== 0) : [];

  // Al abrir, selecciona la primera fuente.
  useEffect(() => {
    if (open && sourceOptions.length && !sourceId) setSourceId(sourceOptions[0].value);
  }, [open, sourceOptions, sourceId]);

  // Precarga: mover todo el saldo disponible de la fuente por talla (con su signo).
  useEffect(() => {
    if (!product || !sourceId) return;
    setMoved(
      stockAt(product, Number(sourceId))
        .filter((s) => s.qty !== 0)
        .map((s) => ({ barcode: s.barcode, qty: s.qty })),
    );
  }, [product, sourceId]);

  // Mapeo por defecto: casa por título de talla; deja vacío lo que no coincide.
  useEffect(() => {
    if (!product || !sourceId || !targetProduct) return;
    const next = {};
    stockAt(product, Number(sourceId))
      .filter((s) => s.qty !== 0)
      .forEach((s) => {
        next[s.barcode] = targetProduct.variants.find((v) => v.title === s.title)?.barcode ?? '';
      });
    setMapping(next);
  }, [product, sourceId, targetProduct]);

  const handleSearch = (family) =>
    getProducts({ s: family, empty: true }).then((r) => {
      const list = r.data.data.filter((p) => p.id !== product.id);
      if (list.length === 1) {
        setTargetProduct(list[0]);
        setOptionList([]);
      } else {
        setTargetProduct(null);
        setOptionList(list.map(({ family }) => family));
      }
    });

  const handleClose = () => {
    setTargetProduct(null);
    setOptionList([]);
    setMoved([]);
    setMapping({});
    setSourceId('');
    onClose();
  };

  const qtyOf = (barcode) => moved.find((m) => m.barcode === barcode)?.qty ?? 0;

  const lines = sourceRows
    .map((s) => ({ sourceBarcode: s.barcode, targetBarcode: mapping[s.barcode], qty: qtyOf(s.barcode) }))
    .filter((l) => l.qty !== 0 && l.targetBarcode);

  // Avisos (no bloqueantes salvo que no haya ninguna línea válida).
  const hasMismatch = targetProduct && sourceRows.some((s) => !targetProduct.variants.some((v) => v.title === s.title));
  const movesWithoutTarget = sourceRows.some((s) => qtyOf(s.barcode) !== 0 && !mapping[s.barcode]);
  const destinoCounts = {};
  sourceRows.forEach((s) => {
    const t = mapping[s.barcode];
    if (t) destinoCounts[t] = (destinoCounts[t] || 0) + 1;
  });
  const hasDuplicateTarget = Object.values(destinoCounts).some((c) => c > 1);

  const handleSubmit = () => {
    if (!targetProduct || !lines.length) return;
    setLoading(true);
    const payloadLines = lines.map(({ sourceBarcode, targetBarcode, qty }) => ({ sourceBarcode, targetBarcode, qty }));
    const req = isLosses
      ? postReclassifyLosses(product.id, { targetProductId: targetProduct.id, lines: payloadLines })
      : postReclassify(product.id, {
          targetProductId: targetProduct.id,
          depositId: Number(sourceId),
          lines: payloadLines,
        });
    req
      .then(() => {
        dispatchToast({ type: SET_OK, payload: { message: 'Mercancía reclasificada' } });
        onDone && onDone();
        handleClose();
      })
      .catch((e) =>
        dispatchToast({ type: SET_ERROR, payload: { message: e?.response?.data?.message || 'Error al reclasificar' } }),
      )
      .finally(() => setLoading(false));
  };

  if (!product) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      large
      title={
        <>
          <Text variant="headingLg" fontWeight="bold">
            Reclasificar mercancía a otra serie-color
          </Text>
          <Text variant="bodySm" tone="subdued">
            Genera movimientos tipo 480 de liquidación entre series.
          </Text>
        </>
      }
      subtitle={product.family}
      primaryAction={{
        content: 'Reclasificar',
        onAction: handleSubmit,
        disabled: !targetProduct || !lines.length,
        loading,
      }}
      secondaryActions={[{ content: 'Cancelar', onAction: handleClose }]}
    >
      <Modal.Section>
        {!sourceOptions.length && (
          <Banner status="info">Esta familia no tiene mercancía (ni extraviados) que reclasificar.</Banner>
        )}

        {sourceOptions.length > 0 && (
          <div className="grid grid-template-columns-1 md:grid-cols-2 gap-12">
            <LegacyStack vertical spacing="tight">
              <Text variant="bodyMd" tone="subdued">
                Origen (depósito o extraviados)
              </Text>
              <Select options={sourceOptions} value={sourceId} onChange={setSourceId} />
            </LegacyStack>
            <LegacyStack vertical spacing="tight">
              <Text variant="bodyMd" tone="subdued">
                Familia destino
              </Text>
              {!targetProduct && (
                <ProductFindCard
                  onSearch={handleSearch}
                  onClearSearch={() => {
                    setTargetProduct(null);
                    setOptionList([]);
                  }}
                  product={null}
                  optionList={optionList}
                  design="inline"
                  inputDesign="inline"
                />
              )}

              {targetProduct && (
                <>
                  <LegacyStack horizontal alignment="center" distribution="fill">
                    <ProductMiniStripe product={targetProduct} />
                    <Button variant="plain" onClick={() => setTargetProduct(null)}>
                      Cambiar
                    </Button>
                  </LegacyStack>
                </>
              )}
            </LegacyStack>
          </div>
        )}

        {targetProduct && sourceOptions.length && (
          <LegacyStack vertical alignment="top" distribution="fill">
            <div className="py-2">
              <Divider />
            </div>

            {(hasMismatch || hasDuplicateTarget || movesWithoutTarget) && (
              <>
                <Banner status="info">
                  <Text variant="bodyMd" tone="subdued" fontWeight="bold">
                    Atención:
                  </Text>
                  <LegacyStack vertical spacing="extraTight">
                    {hasMismatch && (
                      <Text status="info">
                        - Las guías de tallas no coinciden. Revisa la talla destino de cada columna.
                      </Text>
                    )}

                    {movesWithoutTarget && (
                      <Text status="info">- Hay talla sin asignación; esos artículos no se moverán.</Text>
                    )}
                    {hasDuplicateTarget && (
                      <Text status="info" fontWeight="bold">
                        - Hay varias tallas apuntando a la misma talla destino (se sumarán en el destino).
                      </Text>
                    )}
                  </LegacyStack>
                </Banner>
              </>
            )}

            <ReclassTable
              sourceRows={sourceRows}
              targetProduct={targetProduct}
              moved={moved}
              setMoved={setMoved}
              mapping={mapping}
              setMapping={setMapping}
              isLosses={isLosses}
            />

            <Text variant="bodySm" tone="subdued" status="info">
              Este cambio requiere reetiquetado. Revíselo antes de confirmar.
            </Text>
          </LegacyStack>
        )}
      </Modal.Section>
    </Modal>
  );
};

export default ReclassifyModal;
